import React from 'react';
import useSkillSearch from './useSkillSearch';

/**
 * SkillAnalyzerSearch
 * Left panel: search input + results list.
 * On click — calls onSelect(skill) to add skill to the pool.
 */

// Helper: skill icon URL — same pattern as SkillDetailHeader.js
const getIconUrl = (icon_id) =>
    `/images/Icon/Skill_Icons/icon_skill_${icon_id}.png`;

// Color pill per skill color/type
const COLOR_PILL = {
    'Unique Skills': 'bg-yellow-900/40 text-yellow-300 border-yellow-700/50',
    'Green Skills': 'bg-emerald-900/40 text-emerald-300 border-emerald-700/50',
    'Blue Skills': 'bg-blue-900/40 text-blue-300 border-blue-700/50',
    'Red Skills': 'bg-rose-900/40 text-rose-300 border-rose-700/50',
    'White Skills': 'bg-slate-700/40 text-slate-300 border-slate-600/50',
};
const getPill = (color) => COLOR_PILL[color] || 'bg-slate-700/40 text-slate-300 border-slate-600/50';
const getShortLabel = (color) => {
    if (!color) return '?';
    if (color.includes('Unique')) return 'Unique';
    if (color.includes('Green')) return 'Green';
    if (color.includes('Blue')) return 'Blue';
    if (color.includes('Red')) return 'Red';
    return color.replace(' Skills', '');
};

const SkillAnalyzerSearch = ({ onSelect, selectedIds = [] }) => {
    const { results, loading, error, query, setQuery } = useSkillSearch();

    return (
        <div className="flex flex-col gap-3">
            {/* Search input */}
            <div className="relative">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none"
                    fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                    type="search"
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    placeholder="Search skill name..."
                    className="w-full pl-9 pr-4 py-2.5 rounded-xl
                        bg-slate-800 border border-slate-600
                        text-slate-100 placeholder-slate-500
                        text-sm focus:outline-none focus:border-violet-500/70
                        transition-colors"
                />
                {loading && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4
                        border-2 border-violet-400 border-t-transparent rounded-full animate-spin" />
                )}
            </div>

            {/* Results list */}
            <div className="flex flex-col gap-1 max-h-[420px] overflow-y-auto pr-1 scrollbar-thin">
                {/* Loading catalog */}
                {loading && (
                    <p className="text-center text-slate-600 text-xs py-6 italic">
                        Loading skill catalog…
                    </p>
                )}
                {/* Error */}
                {error && !loading && (
                    <p className="text-center text-rose-500 text-xs py-6 italic">
                        Error loading catalog
                    </p>
                )}
                {/* Empty query */}
                {!loading && !error && !query && (
                    <p className="text-center text-slate-600 text-xs py-6 italic">
                        Type to search skills…
                    </p>
                )}
                {/* No results */}
                {!loading && query && results.length === 0 && (
                    <p className="text-center text-slate-500 text-xs py-6 italic">
                        No skills found for "{query}"
                    </p>
                )}
                {results.map(skill => {
                    const alreadyAdded = selectedIds.includes(skill.Id_skill);
                    return (
                        <button
                            key={skill.Id_skill}
                            onClick={() => !alreadyAdded && onSelect(skill)}
                            disabled={alreadyAdded}
                            title={alreadyAdded ? 'Already added' : `Add ${skill.Skill_name}`}
                            className={`flex items-center gap-3 p-2 rounded-xl text-left
                                border transition-all duration-150
                                ${alreadyAdded
                                    ? 'opacity-40 cursor-not-allowed border-transparent bg-transparent'
                                    : 'border-slate-700 bg-slate-800/50 hover:border-violet-500/50 hover:bg-slate-700/50 cursor-pointer'
                                }`}
                        >
                            {/* Icon */}
                            <div className="w-9 h-9 rounded-lg overflow-hidden flex-shrink-0 bg-slate-700">
                                <img
                                    src={getIconUrl(skill.icon_id)}
                                    alt={skill.Skill_name}
                                    className="w-full h-full object-contain"
                                    loading="lazy"
                                />
                            </div>

                            {/* Name + pill */}
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-slate-100 truncate leading-tight">
                                    {skill.Skill_name}
                                </p>
                                <span className={`inline-block mt-0.5 text-[9px] font-bold uppercase
                                    tracking-wider px-1.5 py-0.5 rounded-full border
                                    ${getPill(skill.color)}`}>
                                    {getShortLabel(skill.color)}
                                </span>
                            </div>

                            {/* Cost */}
                            {skill.cost > 0 && (
                                <span className="text-[10px] font-mono text-amber-300 flex-shrink-0">
                                    {skill.cost}pt
                                </span>
                            )}

                            {/* Already added check */}
                            {alreadyAdded && (
                                <svg className="w-4 h-4 text-emerald-500 flex-shrink-0"
                                    fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5}
                                        d="M5 13l4 4L19 7" />
                                </svg>
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default SkillAnalyzerSearch;
